/**
 * Base Path Strategy
 *
 * Shared anchor resolution, self-loop, elbow waypoint, and polyline sampling
 * logic used by all path strategies. Extracted to enforce DRY.
 */

import type { Bound } from '@blocksuite/affine/global/gfx';

import type { AnchorPosition, Point } from '../../types';

/**
 * Anchor positions on element edges (normalized 0-1)
 * Pattern from ConnectorEndpointLocations in connector-manager.ts
 */
const EDGE_ANCHORS: Record<string, [number, number]> = {
  top: [0.5, 0],
  right: [1, 0.5],
  bottom: [0.5, 1],
  left: [0, 0.5],
};

const ALL_ANCHORS: Array<[number, number]> = [
  EDGE_ANCHORS.top,
  EDGE_ANCHORS.right,
  EDGE_ANCHORS.bottom,
  EDGE_ANCHORS.left,
];

/** Minimum offset from element edge before turning in elbow paths (px) */
const ELBOW_MIN_OFFSET = 32;

export type Side = 'top' | 'right' | 'bottom' | 'left';

/**
 * Base class with shared logic for all path strategies.
 */
export abstract class BasePathStrategy {
  // ===========================================================================
  // Anchor Resolution
  // ===========================================================================

  /** Gets a specific anchor point for a bound */
  protected getAnchorPoint(bound: Bound, position: AnchorPosition): Point {
    const anchor =
      position === 'auto' ? EDGE_ANCHORS.right : EDGE_ANCHORS[position];
    const [rx, ry] = anchor || EDGE_ANCHORS.right;
    return {
      x: bound.x + rx * bound.w,
      y: bound.y + ry * bound.h,
    };
  }

  /** Gets all 4 anchor points for a bound (edge midpoints) */
  protected getAllAnchors(bound: Bound): Point[] {
    return ALL_ANCHORS.map(([rx, ry]) => ({
      x: bound.x + rx * bound.w,
      y: bound.y + ry * bound.h,
    }));
  }

  /** Resolves anchor points using hints or auto-selecting nearest pair */
  protected findAnchors(
    source: Bound,
    target: Bound,
    sourceHint?: AnchorPosition,
    targetHint?: AnchorPosition
  ): { sourceAnchor: Point; targetAnchor: Point } {
    if (
      sourceHint &&
      sourceHint !== 'auto' &&
      targetHint &&
      targetHint !== 'auto'
    ) {
      return {
        sourceAnchor: this.getAnchorPoint(source, sourceHint),
        targetAnchor: this.getAnchorPoint(target, targetHint),
      };
    }

    if (sourceHint && sourceHint !== 'auto') {
      const sourceAnchor = this.getAnchorPoint(source, sourceHint);
      const targetAnchor = this.findDirectionCompatibleTargetAnchor(
        sourceAnchor,
        sourceHint,
        target
      );
      return { sourceAnchor, targetAnchor };
    }

    if (targetHint && targetHint !== 'auto') {
      const targetAnchor = this.getAnchorPoint(target, targetHint);
      const sourceAnchors = this.getAllAnchors(source);
      const sourceAnchor = this.findNearestTo(targetAnchor, sourceAnchors);
      return { sourceAnchor, targetAnchor };
    }

    return this.findNearestAnchors(source, target);
  }

  /** Finds the nearest point from a set of points to a target point */
  protected findNearestTo(target: Point, points: Point[]): Point {
    let minDist = Infinity;
    let nearest = points[0];

    for (const p of points) {
      const dist = this.distance(p, target);
      if (dist < minDist) {
        minDist = dist;
        nearest = p;
      }
    }

    return nearest;
  }

  /**
   * Chooses an automatic target anchor without reversing immediately after a
   * fixed source port. If source and target overlap on the source axis, route
   * into the nearest perpendicular outside face instead of crossing the card.
   */
  private findDirectionCompatibleTargetAnchor(
    sourceAnchor: Point,
    sourceSide: Exclude<AnchorPosition, 'auto'>,
    target: Bound
  ): Point {
    const targetRight = target.x + target.w;
    const targetBottom = target.y + target.h;
    const targetCenterX = target.x + target.w / 2;
    const targetCenterY = target.y + target.h / 2;

    if (sourceSide === 'right' || sourceSide === 'left') {
      const targetIsForward =
        sourceSide === 'right'
          ? target.x >= sourceAnchor.x
          : targetRight <= sourceAnchor.x;
      if (targetIsForward) {
        return this.getAnchorPoint(
          target,
          sourceSide === 'right' ? 'left' : 'right'
        );
      }

      const targetIsFullyBehind =
        sourceSide === 'right'
          ? targetRight <= sourceAnchor.x
          : target.x >= sourceAnchor.x;
      if (targetIsFullyBehind) {
        return this.getAnchorPoint(target, sourceSide);
      }

      return this.getAnchorPoint(
        target,
        targetCenterY >= sourceAnchor.y ? 'top' : 'bottom'
      );
    }

    const targetIsForward =
      sourceSide === 'bottom'
        ? target.y >= sourceAnchor.y
        : targetBottom <= sourceAnchor.y;
    if (targetIsForward) {
      return this.getAnchorPoint(
        target,
        sourceSide === 'bottom' ? 'top' : 'bottom'
      );
    }

    const targetIsFullyBehind =
      sourceSide === 'bottom'
        ? targetBottom <= sourceAnchor.y
        : target.y >= sourceAnchor.y;
    if (targetIsFullyBehind) {
      return this.getAnchorPoint(target, sourceSide);
    }

    return this.getAnchorPoint(
      target,
      targetCenterX >= sourceAnchor.x ? 'left' : 'right'
    );
  }

  /** Finds the nearest anchor point pair between source and target */
  protected findNearestAnchors(
    source: Bound,
    target: Bound
  ): { sourceAnchor: Point; targetAnchor: Point } {
    const sourceAnchors = this.getAllAnchors(source);
    const targetAnchors = this.getAllAnchors(target);

    let minDist = Infinity;
    let bestSource = sourceAnchors[0];
    let bestTarget = targetAnchors[0];

    for (const sa of sourceAnchors) {
      for (const ta of targetAnchors) {
        const dist = this.distance(sa, ta);
        if (dist < minDist) {
          minDist = dist;
          bestSource = sa;
          bestTarget = ta;
        }
      }
    }

    return { sourceAnchor: bestSource, targetAnchor: bestTarget };
  }

  /**
   * Determines which side of the element the anchor is on.
   * Used by elbow/orthogonal strategies for exit/entry direction.
   */
  protected getAnchorSide(bound: Bound, anchor: Point): Side {
    const cx = bound.x + bound.w / 2;
    const cy = bound.y + bound.h / 2;
    const dx = anchor.x - cx;
    const dy = anchor.y - cy;

    if (Math.abs(dx) > Math.abs(dy)) {
      return dx > 0 ? 'right' : 'left';
    }
    return dy > 0 ? 'bottom' : 'top';
  }

  // ===========================================================================
  // Self-Loop (shared across all strategies)
  // ===========================================================================

  /** Creates a cubic bezier self-loop arc above the element. */
  protected buildSelfLoopPath(bound: Bound): string {
    const { start, end, cp1, cp2 } = this.selfLoopParams(bound);
    return `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`;
  }

  /** Samples points along the self-loop path for hit-testing. */
  protected buildSelfLoopPathPoints(bound: Bound, segments: number): Point[] {
    const { start, end, cp1, cp2 } = this.selfLoopParams(bound);
    const points: Point[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      points.push(this.cubicBezierPoint(start, cp1, cp2, end, t));
    }
    return points;
  }

  private selfLoopParams(bound: Bound) {
    const cx = bound.x + bound.w / 2;
    const top = bound.y;
    const spreadX = Math.min(bound.w * 0.15, 30);
    const loopHeight = Math.min(bound.w * 0.4, 80);
    const cpSpreadX = Math.min(bound.w * 0.45, 90);

    return {
      start: { x: cx - spreadX, y: top },
      end: { x: cx + spreadX, y: top },
      cp1: { x: cx - cpSpreadX, y: top - loopHeight },
      cp2: { x: cx + cpSpreadX, y: top - loopHeight },
    };
  }

  // ===========================================================================
  // Elbow Waypoints (shared by RoundedElbow + Orthogonal)
  // ===========================================================================

  /**
   * Computes intermediate waypoints for an elbow path.
   * Returns 1 point for L-shape or 2 points for Z-shape.
   */
  protected computeElbowWaypoints(
    s: Point,
    t: Point,
    sSide: Side,
    tSide: Side,
    source?: Bound,
    target?: Bound
  ): Point[] {
    const sHorizontal = sSide === 'left' || sSide === 'right';
    const tHorizontal = tSide === 'left' || tSide === 'right';

    if (
      sHorizontal &&
      tHorizontal &&
      source &&
      target &&
      this.needsHorizontalOutsideDetour(s, t, sSide, tSide)
    ) {
      return this.computeHorizontalOutsideDetour(
        s,
        t,
        sSide,
        tSide,
        source,
        target
      );
    }

    // Perpendicular exit → L-shape (1 corner)
    if (sHorizontal !== tHorizontal) {
      return [sHorizontal ? { x: t.x, y: s.y } : { x: s.x, y: t.y }];
    }

    // Same-axis exit → Z-shape (2 corners)
    if (sHorizontal) {
      const sOff = sSide === 'right' ? ELBOW_MIN_OFFSET : -ELBOW_MIN_OFFSET;
      const tOff = tSide === 'right' ? ELBOW_MIN_OFFSET : -ELBOW_MIN_OFFSET;
      const midX = (s.x + sOff + t.x + tOff) / 2;
      return [
        { x: midX, y: s.y },
        { x: midX, y: t.y },
      ];
    }

    const sOff = sSide === 'bottom' ? ELBOW_MIN_OFFSET : -ELBOW_MIN_OFFSET;
    const tOff = tSide === 'bottom' ? ELBOW_MIN_OFFSET : -ELBOW_MIN_OFFSET;
    const midY = (s.y + sOff + t.y + tOff) / 2;
    return [
      { x: s.x, y: midY },
      { x: t.x, y: midY },
    ];
  }

  private needsHorizontalOutsideDetour(
    s: Point,
    t: Point,
    sSide: Side,
    tSide: Side
  ): boolean {
    if (sSide === 'right' && tSide === 'left') {
      return t.x - s.x < ELBOW_MIN_OFFSET * 2;
    }
    if (sSide === 'left' && tSide === 'right') {
      return s.x - t.x < ELBOW_MIN_OFFSET * 2;
    }
    return false;
  }

  /** Routes around vertically stacked/overlapping cards without crossing them. */
  private computeHorizontalOutsideDetour(
    s: Point,
    t: Point,
    sSide: Side,
    tSide: Side,
    source: Bound,
    target: Bound
  ): Point[] {
    const sourceBottom = source.y + source.h;
    const targetBottom = target.y + target.h;
    let corridorY: number;

    if (sourceBottom <= target.y) {
      corridorY = (sourceBottom + target.y) / 2;
    } else if (targetBottom <= source.y) {
      corridorY = (targetBottom + source.y) / 2;
    } else {
      const above = Math.min(source.y, target.y) - ELBOW_MIN_OFFSET;
      const below = Math.max(sourceBottom, targetBottom) + ELBOW_MIN_OFFSET;
      const aboveCost = Math.abs(s.y - above) + Math.abs(t.y - above);
      const belowCost = Math.abs(s.y - below) + Math.abs(t.y - below);
      corridorY = aboveCost <= belowCost ? above : below;
    }

    const sourceOutsideX =
      sSide === 'right'
        ? source.x + source.w + ELBOW_MIN_OFFSET
        : source.x - ELBOW_MIN_OFFSET;
    const targetOutsideX =
      tSide === 'right'
        ? target.x + target.w + ELBOW_MIN_OFFSET
        : target.x - ELBOW_MIN_OFFSET;
    return [
      { x: sourceOutsideX, y: s.y },
      { x: sourceOutsideX, y: corridorY },
      { x: targetOutsideX, y: corridorY },
      { x: targetOutsideX, y: t.y },
    ];
  }

  // ===========================================================================
  // Polyline Sampling (shared by RoundedElbow + Orthogonal for hit-testing)
  // ===========================================================================

  /** Samples equidistant points along a polyline for hit-testing. */
  protected samplePolyline(allPoints: Point[], segments: number): Point[] {
    if (allPoints.length < 2) return [...allPoints];

    let totalLen = 0;
    for (let i = 1; i < allPoints.length; i++) {
      totalLen += this.distance(allPoints[i - 1], allPoints[i]);
    }

    const result: Point[] = [];
    for (let i = 0; i <= segments; i++) {
      const targetDist = (i / segments) * totalLen;
      let accumulated = 0;

      for (let j = 1; j < allPoints.length; j++) {
        const segLen = this.distance(allPoints[j - 1], allPoints[j]);
        if (accumulated + segLen >= targetDist) {
          const t = (targetDist - accumulated) / segLen;
          result.push({
            x: allPoints[j - 1].x + (allPoints[j].x - allPoints[j - 1].x) * t,
            y: allPoints[j - 1].y + (allPoints[j].y - allPoints[j - 1].y) * t,
          });
          break;
        }
        accumulated += segLen;
      }
    }

    if (result.length <= segments) {
      result.push(allPoints[allPoints.length - 1]);
    }

    return result;
  }

  // ===========================================================================
  // Control Point Direction (per-anchor exit/entry direction)
  // ===========================================================================

  /**
   * Resolves which side an anchor is on, using explicit hint or geometric detection.
   */
  protected resolveAnchorSide(
    bound: Bound,
    anchor: Point,
    hint?: AnchorPosition
  ): Side {
    if (hint && hint !== 'auto') return hint as Side;
    return this.getAnchorSide(bound, anchor);
  }

  /**
   * Computes a control point that exits/enters perpendicular to the anchor side.
   * Source uses this for the exit tangent, target for the approach tangent.
   */
  protected controlPointOutward(
    point: Point,
    side: Side,
    offset: number
  ): Point {
    switch (side) {
      case 'right':
        return { x: point.x + offset, y: point.y };
      case 'left':
        return { x: point.x - offset, y: point.y };
      case 'bottom':
        return { x: point.x, y: point.y + offset };
      case 'top':
        return { x: point.x, y: point.y - offset };
    }
  }

  // ===========================================================================
  // Math Primitives
  // ===========================================================================

  /** Euclidean distance between two points */
  protected distance(a: Point, b: Point): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /** Cubic bezier interpolation for path point sampling */
  protected cubicBezierPoint(
    p0: Point,
    p1: Point,
    p2: Point,
    p3: Point,
    t: number
  ): Point {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;

    return {
      x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
      y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
    };
  }
}
